import { useMemo, useRef, useState } from 'react';
import type { LadderStep } from '@tariff/generator';
import type { LadderProposalDto } from '../lib/api';
import { useKeyMap, type Binding } from '../lib/keys';
import { useT, type MessageKey } from '../lib/i18n';
import { money } from '../lib/format';

/**
 * The generator review screen.
 *
 * The brief asked for conflict resolution between a generated ladder and a
 * hand-edited one, possibly advanced. A three-way merge is where that class of
 * feature usually dies: expensive to build, hard to test, and — the part that
 * actually sinks it — operators do not trust a tool whose reasoning they cannot
 * see.
 *
 * So the unit of decision here is ONE RATE, shown before and after, accepted or
 * rejected on its own. Nothing is applied until the operator says so, rates a
 * human has edited are flagged rather than quietly overwritten, and the server
 * recomputes the proposal when applying it, so what is accepted is exactly what
 * was shown.
 */

/**
 * ---------------------------------------------------------------------------
 * WHY EACH ENTRY IS A ROW OF FIELDS AND NOT A NUMBER
 * ---------------------------------------------------------------------------
 *
 * A uniform ladder — 3 nights 5%, 7 nights 10% — is what a generator produces
 * and almost never what an operator ends up selling. The real card has a fixed
 * summer week at a round number, an early-booking version of the same length
 * with a different arrival window, and a long-stay discount that runs outside
 * the school holidays only.
 *
 * So an entry gets its own row: a name, a length, a price that is EITHER a
 * percentage or a flat amount, and the arrivals it covers. The uniform ladder
 * is still three short rows, because every field but the length is optional.
 *
 * Two entries of the same length whose arrival windows overlap are shown as a
 * conflict on the row itself, because the engine has no precedence — it takes
 * the cheaper tiling, so the dearer entry would silently never apply.
 */

/** Adding a row needs an id that no existing row is using. */
function freshId(steps: readonly LadderStep[], nights: number): string {
  const base = `${nights}n`;
  if (!steps.some((s) => s.id === base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`;
    if (!steps.some((s) => s.id === candidate)) return candidate;
  }
}

const idOf = (s: LadderStep, i: number) => s.id ?? `${s.nights}n-${i}`;

/** What the diff says happened to a rate: a closed set, so translatable. */
const CHANGE_KIND: Record<string, MessageKey> = {
  added: 'ladder.kindAdded',
  removed: 'ladder.kindRemoved',
  changed: 'ladder.kindChanged',
};

export interface LadderReviewProps {
  steps: LadderStep[];
  onSteps: (steps: LadderStep[]) => void;
  proposal: LadderProposalDto | null;
  onPropose: () => void;
  onApply: (accept: string[]) => void;
  busy?: boolean;
  readOnly?: boolean;
  /**
   * Where the rungs came from: read off the card, or a default standing in.
   *
   * A default shown without saying so is the bug the park reported — three
   * invented rungs beside a card with twelve rates, and no way to tell.
   */
  basis?: 'card' | 'premium' | 'nothing';
  /** The card has changed since these rungs were read off it. */
  stale?: boolean;
  /** Read them again from the card as it is now, discarding hand edits. */
  onReread?: () => void;
}

export function LadderReview({
  steps, onSteps, proposal, onPropose, onApply, busy, readOnly, stale, onReread,
  basis = 'card',
}: LadderReviewProps) {
  const t = useT();
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  /**
   * The card moved under a ladder somebody was editing.
   *
   * The rungs are read off the card, so an edit elsewhere — a week price
   * changed on the Programme tab, a package added — makes them a description of
   * a card that no longer exists, and Preview then measures its proposal against
   * prices nobody charges. Silently re-reading would delete the operator's own
   * rungs; silently keeping them is what used to happen, and it was wrong in the
   * direction nobody notices.
   */
  const staleBanner = stale && !readOnly ? (
    <div className="banner warn" role="status">
      {t('ladder.staleNote')}
      <button type="button" className="btn" onClick={onReread}>{t('ladder.reread')}</button>
    </div>
  ) : null;

  const changes = useMemo(
    () => (proposal?.diff.rates ?? []).filter((r) => r.kind !== 'unchanged'),
    [proposal],
  );
  const protectedIds = useMemo(
    () => new Set(proposal?.protected ?? []),
    [proposal],
  );

  const toggle = (id: string) => setAccepted((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const setStep = (i: number, patch: Partial<LadderStep>) =>
    onSteps(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const [cursor, setCursor] = useState(0);
  const hostRef = useRef<HTMLDivElement>(null);

  /** Conflicts from the last preview, indexed by the entry they belong to. */
  const problems = useMemo(() => {
    const byStep = new Map<string, string[]>();
    for (const p of proposal?.problems ?? []) {
      const list = byStep.get(p.stepId);
      if (list) list.push(p.message); else byStep.set(p.stepId, [p.message]);
    }
    return byStep;
  }, [proposal]);

  const addStep = (after?: number) => {
    const nights = after === undefined ? 7 : steps[after]?.nights ?? 7;
    const entry: LadderStep = { id: freshId(steps, nights), nights, discountPct: 10 };
    const at = after === undefined ? steps.length : after + 1;
    onSteps([...steps.slice(0, at), entry, ...steps.slice(at)]);
    setCursor(at);
  };

  const removeStep = (i: number) => {
    onSteps(steps.filter((_, j) => j !== i));
    setCursor((c) => Math.max(0, Math.min(c, steps.length - 2)));
  };

  /**
   * A duplicate is the fastest route to the shape operators actually want:
   * two entries of the same length with different windows. It deliberately
   * leaves the windows alone, so the conflict shows immediately and says what
   * needs changing, rather than the tool guessing a split.
   */
  const duplicateStep = (i: number) => {
    const s = steps[i];
    if (!s) return;
    const copy: LadderStep = {
      ...s,
      id: freshId(steps, s.nights),
      // The label is the operator's; only the word for "copy" is ours.
      label: s.label ? t('ladder.copySuffix', { label: s.label }) : undefined,
    };
    onSteps([...steps.slice(0, i + 1), copy, ...steps.slice(i + 1)]);
    setCursor(i + 1);
  };

  useKeyMap(useMemo<Binding[]>(() => [
    {
      combo: 'mod+enter', label: 'ladder.keyPreview', group: 'keys.groupLadder',
      inFields: true, run: () => { if (!busy) onPropose(); },
    },
    {
      combo: 'alt+n', label: 'ladder.keyAddEntry', group: 'keys.groupLadder',
      inFields: true, when: () => !readOnly, run: () => addStep(steps.length - 1),
    },
    {
      combo: 'alt+d', label: 'ladder.keyDuplicateEntry', group: 'keys.groupLadder',
      inFields: true, when: () => !readOnly && steps.length > 0,
      run: () => duplicateStep(cursor),
    },
    {
      combo: 'alt+backspace', label: 'ladder.keyRemoveEntry', group: 'keys.groupLadder',
      inFields: true, when: () => !readOnly && steps.length > 0,
      run: () => removeStep(cursor),
    },
    {
      combo: 'alt+arrowdown', label: 'ladder.keyNextEntry', group: 'keys.groupLadder',
      inFields: true, run: () => setCursor((c) => Math.min(steps.length - 1, c + 1)),
    },
    {
      combo: 'alt+arrowup', label: 'ladder.keyPreviousEntry', group: 'keys.groupLadder',
      inFields: true, run: () => setCursor((c) => Math.max(0, c - 1)),
    },
  ], [steps, cursor, busy, readOnly, onPropose]));

  const basisBanner = basis === 'card' ? null : (
    <div className="banner" role="status">
      {t(basis === 'premium' ? 'ladder.basisPremium' : 'ladder.basisNothing')}
    </div>
  );

  return (
    <div className="stack">
      {staleBanner}
      {basisBanner}
      <section className="panel">
        <div className="panel-hd">
          <h2>{t('ladder.heading')}</h2>
          <span className="grow" />
          <span className="note">{t('ladder.note')}</span>
        </div>
        <div className="panel-bd">
          <div className="scroll-x" ref={hostRef}>
            <table className="losTable">
              <thead>
                <tr>
                  <th>{t('ladder.columnName')}</th>
                  <th className="r">{t('ladder.columnNights')}</th>
                  <th>{t('ladder.columnPrice')}</th>
                  <th>{t('ladder.columnArrivals')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {steps.map((s, i) => {
                  const id = idOf(s, i);
                  const conflicts = problems.get(id) ?? [];
                  const flat = s.price !== undefined;
                  return (
                    <tr
                      key={id}
                      className={`${i === cursor ? 'on' : ''}${conflicts.length ? ' bad' : ''}`}
                      onFocus={() => setCursor(i)}
                    >
                      <td>
                        <input
                          className="mid" type="text" value={s.label ?? ''}
                          placeholder={t('ladder.entryNamePlaceholder', { nights: s.nights })}
                          aria-label={t('ladder.entryName', { number: i + 1 })}
                          disabled={readOnly}
                          onChange={(e) => setStep(i, { label: e.target.value || undefined })}
                        />
                      </td>
                      <td className="r">
                        <input
                          className="tiny" type="number" min={2} max={90} value={s.nights}
                          aria-label={t('ladder.entryNights', { number: i + 1 })}
                          disabled={readOnly}
                          onChange={(e) => setStep(i, { nights: Number(e.target.value) || 2 })}
                        />
                      </td>
                      <td>
                        <div className="row tight">
                          {/* One control, two meanings. A percentage and a flat
                              amount are alternatives, not two fields that could
                              both be filled in — the generator rejects an entry
                              that sets both, so the UI never offers it. */}
                          <select
                            className="tiny"
                            aria-label={t('ladder.entryBasis', { number: i + 1 })}
                            value={flat ? 'flat' : 'pct'}
                            disabled={readOnly}
                            onChange={(e) => setStep(i, e.target.value === 'flat'
                              ? { price: 399, discountPct: undefined }
                              : { discountPct: 10, price: undefined })}
                          >
                            <option value="pct">{t('ladder.entryBasisPercent')}</option>
                            <option value="flat">{t('ladder.entryBasisFlat')}</option>
                          </select>
                          {flat ? (
                            <input
                              className="tiny" type="number" min={0} step={5} value={s.price ?? 0}
                              aria-label={t('ladder.entryPrice', { number: i + 1 })}
                              disabled={readOnly}
                              onChange={(e) => setStep(i, { price: Number(e.target.value) || 0 })}
                            />
                          ) : (
                            <input
                              className="tiny" type="number" min={0} max={99} value={s.discountPct ?? 0}
                              aria-label={t('ladder.entryDiscount', { number: i + 1 })}
                              disabled={readOnly}
                              onChange={(e) => setStep(i, { discountPct: Number(e.target.value) || 0 })}
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="row tight">
                          {/* Empty means the whole band, which is the common
                              case, so it stays empty rather than being
                              pre-filled with dates nobody chose. */}
                          <input
                            className="mid" type="date" value={s.arrive?.from ?? ''}
                            aria-label={t('ladder.entryFirstArrival', { number: i + 1 })}
                            disabled={readOnly}
                            onChange={(e) => setStep(i, e.target.value
                              ? { arrive: { from: e.target.value, until: s.arrive?.until ?? e.target.value } }
                              : { arrive: undefined })}
                          />
                          <span className="note">→</span>
                          <input
                            className="mid" type="date" value={s.arrive?.until ?? ''}
                            aria-label={t('ladder.entryLastArrival', { number: i + 1 })}
                            disabled={readOnly || !s.arrive}
                            onChange={(e) => setStep(i, s.arrive
                              ? { arrive: { from: s.arrive.from, until: e.target.value } }
                              : {})}
                          />
                          {!s.arrive && <span className="note">{t('ladder.wholePeriod')}</span>}
                        </div>
                      </td>
                      <td className="r">
                        <button
                          type="button" className="btn" disabled={readOnly}
                          onClick={() => duplicateStep(i)}
                          title={t('ladder.duplicateTitle')}
                        >
                          {t('ladder.duplicate')}
                        </button>
                        <button
                          type="button" className="btn" disabled={readOnly}
                          onClick={() => removeStep(i)}
                        >
                          {t('ladder.remove')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {steps.length === 0 && (
                  <tr>
                    <td colSpan={5} className="note">{t('ladder.noEntries')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {[...problems.entries()].length > 0 && (
            <ul className="problems" style={{ marginTop: 10 }}>
              {[...problems.entries()].flatMap(([id, msgs]) => msgs.map((m) => (
                <li key={`${id}:${m}`} className="bad">{m}</li>
              )))}
            </ul>
          )}

          <div className="row" style={{ marginTop: 12 }}>
            <button
              type="button" className="btn" disabled={readOnly}
              onClick={() => addStep(steps.length - 1)}
            >
              {t('ladder.addEntry')}
            </button>
            <button type="button" className="btn pri" onClick={onPropose} disabled={busy}>
              {busy ? t('ladder.working') : t('ladder.preview')}
            </button>
            <span className="grow" />
            <span className="note">{t('ladder.shortcutHint')}</span>
          </div>

          {proposal && proposal.observed.length > 0 && (
            <>
              <h3 className="eyebrow" style={{ marginTop: 20 }}>{t('ladder.observedHeading')}</h3>
              <p className="note" style={{ marginBottom: 8 }}>{t('ladder.observedNote')}</p>
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr>
                      <th className="r">{t('ladder.observedNights')}</th>
                      <th className="r">{t('ladder.observedTypical')}</th>
                      <th className="r">{t('ladder.observedSpread')}</th>
                      <th className="r">{t('ladder.observedFrom')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposal.observed.map((o) => (
                      <tr key={o.nights}>
                        <td className="r">{o.nights}</td>
                        <td className="r">{o.discountPct}%</td>
                        <td className="r">
                          {o.minPct}% – {o.maxPct}%
                          {o.maxPct - o.minPct > 10 && (
                            <span className="chip bad">{t('ladder.observedWide')}</span>
                          )}
                        </td>
                        <td className="r">{t('ladder.observedSamples', { count: o.samples })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>

      {proposal && proposal.leakage.length > 0 && (
        <section className="panel">
          <div className="panel-hd">
            <h2>{t('ladder.leakageHeading')}</h2>
            <span className="grow" />
            <span className="chip bad">{proposal.leakage.length}</span>
          </div>
          <div className="panel-bd">
            {/* Only leakage upwards is reported. A wide window downwards is
                harmless: a discount cannot win where the nightly rates are
                already cheaper. */}
            <p className="note" style={{ marginBottom: 10 }}>{t('ladder.leakageNote')}</p>
            <div className="scroll-x">
              <table>
                <thead>
                  <tr>
                    <th>{t('ladder.leakageRate')}</th>
                    <th className="r">{t('ladder.leakagePricedFrom')}</th>
                    <th className="r">{t('ladder.leakageReaches')}</th>
                    <th className="r">{t('ladder.leakageDiscount')}</th>
                    <th className="r">{t('ladder.leakageThatPeriod')}</th>
                  </tr>
                </thead>
                <tbody>
                  {proposal.leakage.map((l) => (
                    <tr key={l.rateId}>
                      <td>{l.rateLabel}</td>
                      <td className="r">{t('ladder.perNight', { price: money(l.pricedFrom) })}</td>
                      <td className="r">
                        {t('ladder.reachesInto', {
                          label: l.dearerBandLabel, price: money(l.reachesInto),
                        })}
                      </td>
                      <td className="r">{money(l.discountTotal)}</td>
                      <td className="r">{money(l.dearerTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {proposal && (
        <section className="panel">
          <div className="panel-hd">
            <h2>{t('ladder.proposedHeading')}</h2>
            <span className="grow" />
            {changes.length === 0
              ? <span className="pill ok">{t('ladder.nothingToChange')}</span>
              : (
                <>
                  <button
                    type="button" className="btn"
                    onClick={() => setAccepted(new Set(changes.filter((c) => !protectedIds.has(c.id)).map((c) => c.id)))}
                  >
                    {t('ladder.acceptAllUnedited')}
                  </button>
                  <button type="button" className="btn" onClick={() => setAccepted(new Set())}>
                    {t('ladder.clear')}
                  </button>
                  <button
                    type="button" className="btn pri"
                    disabled={busy || readOnly || accepted.size === 0}
                    onClick={() => onApply([...accepted])}
                    title={readOnly ? t('ladder.applyBlocked') : undefined}
                  >
                    {/* The count is part of the label rather than glued to the
                        end of it: "3 toepassen" puts it first in Dutch. */}
                    {accepted.size
                      ? t('ladder.applyCount', { count: accepted.size })
                      : t('ladder.apply')}
                  </button>
                </>
              )}
          </div>
          <div className="panel-bd">
            {changes.length === 0 ? (
              <p className="note">{t('ladder.alreadyMatches')}</p>
            ) : (
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 34 }} />
                      <th>{t('ladder.changeRate')}</th>
                      <th>{t('ladder.changeKind')}</th>
                      <th className="r">{t('ladder.changeBefore')}</th>
                      <th className="r">{t('ladder.changeAfter')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.map((c) => (
                      <tr key={c.id} className={accepted.has(c.id) ? 'picked' : undefined}>
                        <td>
                          <input
                            type="checkbox"
                            checked={accepted.has(c.id)}
                            onChange={() => toggle(c.id)}
                            aria-label={t('ladder.accept', { label: c.label })}
                            disabled={readOnly}
                          />
                        </td>
                        <td>
                          {c.label}
                          {protectedIds.has(c.id) && (
                            <span className="chip bad" title={t('ladder.handEditedTitle')}>
                              {t('ladder.handEdited')}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`chip k-${c.kind}`}>
                            {CHANGE_KIND[c.kind] ? t(CHANGE_KIND[c.kind]) : c.kind}
                          </span>
                        </td>
                        <td className="r">
                          {c.before?.price?.base != null ? money(c.before.price.base) : '—'}
                        </td>
                        <td className="r">
                          {c.after?.price?.base != null ? money(c.after.price.base) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
